<template>
  <div
    id="db-results"
    class="db-funnel-results db-package-config-results"
  >
    <h1 class="db-title-2 db-package-config-title">
      {{
        isConfigurableFunctionRoom() ? $t('Tell us your preferences') : $t('Your package details')
      }}
    </h1>

    <p class="db-text">
      {{
        isConfigurableFunctionRoom()
          ? $t(
              'We would like you to have the best event possible. Please review all details and let us know what your preferences are. We will do our best to meet your requirements.',
            )
          : $t(
              'We would like you to have the best event possible, please review all details before proceeding.',
            )
      }}
    </p>

    <div v-if="optionsTimeSlot.length > 1">
      <h2 class="db-title-3 db-uppercase db-package-config-subtitle db-tw-mb-3">
        {{ $t('Select your preferred time slot') }}
      </h2>
      <p class="db-text">
        {{ $t('This package is available at several times, please choose your preferred slot:') }}
      </p>
      <DbSelectCustom
        class="db-tw-w-full 2md:db-tw-w-2/5 db-timeslot"
        :modelValue="selectedTimeSlot"
        :options="optionsTimeSlot"
        v-on:update:model-value="setTimeSlot"
      >
        <template v-slot:current="{ label }">
          <IconDbClock
            width="28"
            height="28"
          />
          {{ label }}
        </template>
        <template v-slot:option="option">
          {{ option.option.label }}
          <div
            class="db-tw-ml-auto"
            v-if="!!(option.option as any).price"
          >
            {{ (option.option as any).price }}
          </div>
          <div
            class="db-tw-ml-auto"
            v-else-if="(option.option as any).price === null && (option.option as any).price !== ''"
          >
            <DbSpinner size="24" />
          </div>
        </template>
      </DbSelectCustom>
    </div>

    <div v-if="isConfigurableFunctionRoom()">
      <h2 class="db-title-3 db-uppercase db-package-config-subtitle db-tw-mb-3">
        {{ $t('Select your function room setup') }}
      </h2>

      <DbFunctionRoomSelector
        :modelValue="currentRoomSetup"
        :labelRoomSelector="$t('Please select your function room setup (Mandatory field):')"
        :checkbox="functionRoomSetupOptions"
        v-on:update:model-value="setRoomSetup"
      />
    </div>

    <h2
      v-if="isConfigurableAddOns() || isConfigurableFunctionRoom()"
      class="db-title-3 db-uppercase db-package-config-subtitle"
    >
      {{ isConfigurableAddOns() ? $t('Configure your Package') : $t('Event information') }}
    </h2>

    <p
      v-if="isConfigurableAddOns() || isConfigurableFunctionRoom()"
      class="db-text"
    >
      {{
        isConfigurableAddOns()
          ? $t(
              'Below is a breakdown of what is included with your package selection. Please feel free to add any additional items to customize your event.',
            )
          : $t('Below are the details included with your package selection')
      }}
    </p>
    <ul
      class="db-funnel-results-list"
      v-if="!isLoading"
    >
      <li
        v-for="event in packageEvents"
        :key="event.id"
      >
        <PackageCardEvent
          :event="event.eventClassification"
          :items="event.items"
          :addons="cardAddons[event.id]"
          :hasAddons="(event.addOnItems?.filter((item) => item.unitPrice > 0).length ?? 0) > 0"
          :time-slot="getItemTimeSlot(event.eventClassification.id)"
          v-on:edit="openModal(event.id)"
          v-on:delete="deleteAddon(event.id, $event)"
          v-on:add-more="openModal(event.id)"
        />
      </li>
    </ul>
    <div
      class="db-funnel-results-list db-tw-m-auto"
      v-else
    >
      <DbSpinner size="50" />
    </div>
    <button
      type="button"
      class="db-button-primary db-button-large db-package-config-continue"
      v-on:click="funnelRouter.goToCheckout"
      v-bind:disabled="isLoading"
    >
      {{ $t('Continue') }}
    </button>
  </div>

  <!-- Modal addons selection -->
  <DbModal
    v-if="modals.isOpen && modals.addons"
    class="db-modal-addons"
    v-on:close-modal="modals.isOpen = false"
  >
    <template v-slot:header>
      <h1 class="db-title-1">
        {{ $t('Select all your add-ons') }}
      </h1>
    </template>

    <PackageModalAddons
      :addons="modals.addons"
      :selectedAddons="cardAddons[currentEventID]"
      v-on:close="modals.isOpen = false"
      v-on:submit="addToCart($event)"
    />
  </DbModal>
  <!-- Modal addons selection -->
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue'
import type {
  CardAddons,
  PackageModal,
  PackageTimeSlot,
} from './packageConfiguration/types/packageData'
import { useConfigurations } from '@/stores/configurations'
import { useCartStore } from '@/stores/cart'
import type { BestFitModeTimeSlot } from '@/stores/cart'
import { storeToRefs } from 'pinia'
import { useFunnelRouter } from '@/AppBookingFunnel/composables/funnel-router'
import { usePackagesStore } from '@/stores/packages'
import IconDbDefaultFunctionRoomSetup from '~icons/db/function-roomsetup-default?raw'
import { useI18n } from 'vue-i18n'
import { timeSlotFormatting, priceSlotFormatting } from '@/services/config-date'
import type { GalaxyMeetingRoom } from '@/types/galaxy-data/meeting-room'
import { isIdenticalArrays } from '@/utils/utils'
import { useModalsStore } from '@/stores/modals'
import { useGtm } from '@/composables/features/useGtm'

const configStore = useConfigurations()
const funnelRouter = useFunnelRouter()

const cartStore = useCartStore()
const modalStore = useModalsStore()
const packagesStore = usePackagesStore()
const { isEventConfigurable } = storeToRefs(configStore)
const { event } = useGtm()

const {
  cartSummary,
  currentFunctionRoom,
  packageEvents,
  cart,
  getBestFitTimeSlot,
  currentFunctionRoomId,
  currencyCode,
  isLoading,
} = storeToRefs(cartStore)
const { meetingRoomID } = storeToRefs(packagesStore)

const FunctionRoomSetups = packagesStore.getDb2FunctionRoomSetups()
const functionRoomSetupOptionsValue = currentFunctionRoom.value?.apiData?.functionRoomSetups
const functionRoomTimeSlot = configStore.funnel.showVenueSelection
  ? currentFunctionRoom.value?.apiData.searchTimeSlot
  : getBestFitTimeSlot.value
const functionRoomSetupOptions = functionRoomSetupOptionsValue?.map((f_setup) => {
  const defaultFunctionRoomSetups = {
    id: -1,
    cle: f_setup,
    name: f_setup,
    default_icon: IconDbDefaultFunctionRoomSetup,
  }
  return FunctionRoomSetups.find((item) => item.cle === f_setup) || defaultFunctionRoomSetups
})

const currentRoomSetup = ref(functionRoomSetupOptions?.[0].cle)
if (functionRoomSetupOptions?.[0].cle)
  cartStore.setFunctionRoomSetup(functionRoomSetupOptions?.[0].cle)

/* Get value from on change SELECT YOUR FUNCTION ROOM SETUP */
function setRoomSetup(val: string) {
  currentRoomSetup.value = val
  cartStore.setFunctionRoomSetup(val)
}

const modals: PackageModal = reactive({
  isOpen: false,
  addons: [], // all addOnitem for modals
})

/* Page Data */
const currentEventID = ref('')

// TODO: MUST FIX, this is ugly to unlock, but really needs to be fixed
const cardAddons = computed<CardAddons>({
  get() {
    return cart.value?.addons || {}
  },
  set(newValue) {
    cartStore.setAddons(newValue)
    // see below setAddons is also used later
  },
})

const { t } = useI18n()

// Collect all the options for the timeSlot select
const optionsTimeSlot = computed(() => {
  // Check that a functionRoom is actually selected, otherwise return no option
  if (!currentFunctionRoomId.value) {
    return new Array<{
      label: string
      price?: string | null
      value: number
      bestFitMode?: BestFitModeTimeSlot
    }>({ label: '', value: -1, bestFitMode: undefined })
  }
  const arrayTime: Array<{
    label: string
    price?: string | null
    value: number
    bestFitMode?: BestFitModeTimeSlot
  }> = []
  functionRoomTimeSlot?.forEach((timeSlot, index) => {
    let newTimeSlot: {
      label: string
      price?: string | null
      value: number
      bestFitMode?: BestFitModeTimeSlot | undefined
    } = {
      label: '',
      value: 0,
    }
    // Determine if the format of timeSlot is "startTime to endTime" or Only "startTime"
    if (packagesStore.displayStartEndTimeTimeSlot() < 2) {
      newTimeSlot = {
        label: t('{x} to {y}', {
          x: timeSlotFormatting(timeSlot.startTime, false, t),
          y: timeSlotFormatting(timeSlot.endTime, false, t),
        }),
        value: index,
      }
    } else {
      newTimeSlot = {
        label: timeSlotFormatting(timeSlot.startTime, false, t),
        value: index,
      }
    }
    // When BestFitMode Enable, add relative price information
    if (!configStore.funnel.showVenueSelection) {
      newTimeSlot = {
        ...newTimeSlot,
        price: priceSlotFormatting(
          currentFunctionRoomId.value,
          getBestFitTimeSlot.value?.at(index)?.functionRoomId,
          getBestFitTimeSlot.value?.at(index)?.priceCorrespondence,
          currencyCode.value || 'USD',
        ),
        value: index,
        bestFitMode: timeSlot as BestFitModeTimeSlot,
      }
    }
    arrayTime.push(newTimeSlot)
  })
  // If at least one timeSlot exist, then define it in the cardStorage (to display the field)
  if (arrayTime.length > 1) {
    // When BestFitMode Enable
    if (!configStore.funnel.showVenueSelection) {
      // Look for the "defaultSelected" timeSlot
      if (arrayTime.filter((e) => e.bestFitMode?.defaultSelected === true).length !== 0) {
        cartStore.setTimeSlot({
          label: arrayTime.filter((e) => e.bestFitMode?.defaultSelected === true)[0].label,
          index: 0,
        })
      }
    } else {
      cartStore.setTimeSlot({
        label: arrayTime[0].label,
        index: 0,
      })
    }
    // Default value when there is no timeSlot
  } else if (arrayTime.length === 0) {
    return new Array<{
      label: string
      price?: string | null
      value: number
      bestFitMode?: BestFitModeTimeSlot
    }>({ label: '', value: -1, bestFitMode: undefined })
  }
  return arrayTime
})

// This variable store the index of "select field"
const selectedTimeSlot = ref<number>(
  optionsTimeSlot.value.length
    ? // When BestFit is enable
      !configStore.funnel.showVenueSelection
      ? // Look for the defaultSelected one (return -1 when not found)
        optionsTimeSlot.value.findIndex((e) => e.bestFitMode?.defaultSelected === true)
      : // Otherwise take the firstOne
        optionsTimeSlot.value[0].value
    : -1,
)

// This variable store the "original" index of the selected timeSlot for the current functionRoom
// (If bestFitMode is disable, this variable is always equal to the one above "selectedTimeSlot")
// (If bestFitMode is enable, this variable contains the index of timeSlot corresponding to the functionRoom list of timeSlot)
const selectedTimeSlotOriginalIndex = ref<number>(
  optionsTimeSlot.value.length ? optionsTimeSlot.value[0].value : -1,
)

/**
 * When selected a timeSlot, define the value
 * @param selected select value
 */
async function setTimeSlot(selected: string | number) {
  if (typeof selected === 'number' && selected >= 0 && optionsTimeSlot.value.at(selected)) {
    // When BestFitMode Enable
    if (!configStore.funnel.showVenueSelection) {
      // Check variable data are correctly defined
      if (
        !optionsTimeSlot.value[selected].bestFitMode?.functionRoomId ||
        optionsTimeSlot.value[selected].bestFitMode?.functionRoomId === undefined
      ) {
        throw new Error('bestFitMode: No functionRoomId for this timeSlot')
      }
      // Check if the timeSlot selected correspond to a new functionRoom
      if (
        currentFunctionRoomId.value !== optionsTimeSlot.value[selected].bestFitMode?.functionRoomId
      ) {
        // Check if the new functionRoom has the seem AddOns And If not so, ask confirmation to change
        if (
          selectedTimeSlot.value !== -1 &&
          isIdenticalArrays(
            optionsTimeSlot.value[selected].bestFitMode?.listAddOnID ?? [],
            optionsTimeSlot.value[selectedTimeSlot.value].bestFitMode?.listAddOnID ?? [],
          ) === false
        ) {
          const response = await modalStore.confirm({
            title: t('Are you sure?'),
            body: t('This action will reset your addOns'),
            confirmButton: t('Yes, select this timeslot'),
            cancelButton: t('No, keep it like that'),
          })
          // If The response is No: do nothing
          if (!response) {
            return
          }
          // Clear the addOns
          cardAddons.value = {}
          cartStore.setAddons({})
        }
        // Select the new functionRoom/venue in cart
        cartStore.setFunctionRoom(optionsTimeSlot.value[selected].bestFitMode?.functionRoomId)
        // Set the meetingRoomID in query param URL
        meetingRoomID.value = {
          db2_eventroomid: optionsTimeSlot.value[selected].bestFitMode?.functionRoomId,
        } as GalaxyMeetingRoom
      }
      // Define the select index to the "select field"
      selectedTimeSlot.value = optionsTimeSlot.value[selected].value
      selectedTimeSlotOriginalIndex.value =
        optionsTimeSlot.value[selected].bestFitMode?.originalIndex || 0
      cartStore.setTimeSlot({
        label: optionsTimeSlot.value[selected].label,
        index: selectedTimeSlotOriginalIndex.value,
      })
      return
    }
    // When BestFitMode is disable:
    // Define the select index to the "select field"
    selectedTimeSlot.value = optionsTimeSlot.value[selected].value
    selectedTimeSlotOriginalIndex.value = optionsTimeSlot.value[selected].value
    cartStore.setTimeSlot({
      label: optionsTimeSlot.value[selected].label,
      index: selected,
    })
  }
}

// Handle the correspondence between timeSlot selected and item timeSlot
function getItemTimeSlot(eventClassificationID: string): PackageTimeSlot | undefined {
  // Check that at least one timeSlot exist and eventClassificationID exist
  if (eventClassificationID.length === 0 || selectedTimeSlotOriginalIndex.value < 0) {
    return undefined
  }
  // Looking for the eventClassificationID
  for (const eventID of currentFunctionRoom.value?.apiData.eventsIds ?? []) {
    // Check that the selected time index exist for this eventClassificationID
    if (
      eventID.eventClassificationId === eventClassificationID &&
      eventID.timeSlot.at(selectedTimeSlotOriginalIndex.value) &&
      eventID.timeSlot.at(selectedTimeSlotOriginalIndex.value)?.startTime &&
      eventID.timeSlot.at(selectedTimeSlotOriginalIndex.value)?.endTime
    ) {
      return {
        startTime: eventID.timeSlot[selectedTimeSlotOriginalIndex.value].startTime,
        endTime: eventID.timeSlot[selectedTimeSlotOriginalIndex.value].endTime,
      }
    }
  }
  return undefined
}

/**
 * onClick on add more link
 * @param eventId
 * open modal with list of eventId addons
 */
const openModal = (eventId: string) => {
  /* open modal */
  modals.isOpen = true
  /* store current event id */
  currentEventID.value = eventId
  /* get addons data from packageEvents based on currentEventId */
  const currentCardEventData = packageEvents.value?.filter(
    (data: { id: string }) => data.id === eventId,
  )

  if (!currentCardEventData) {
    throw new Error('data issue')
  }
  /* apply it to modals object */
  modals.addons = currentCardEventData[0].addOnItems

  if (currentCardEventData[0].addOnItems.length > 0) {
    const hotel = window.db2BookingFunnel.getHotel()
    let attendees_bucket = ''
    if (packagesStore.attendees >= 0 && packagesStore.attendees <= 5) attendees_bucket = '0-5'
    if (packagesStore.attendees >= 6 && packagesStore.attendees <= 10) attendees_bucket = '6-10'
    if (packagesStore.attendees >= 11 && packagesStore.attendees <= 20) attendees_bucket = '11-20'
    if (packagesStore.attendees >= 21 && packagesStore.attendees <= 30) attendees_bucket = '21-30'
    if (packagesStore.attendees > 30) attendees_bucket = '+30'
    event('view_item_list', {
      item_list_id: 'addons_list',
      item_list_name: 'Addons List',
      items: currentCardEventData[0].addOnItems.map((addon, index) => {
        return {
          item_id: addon.sourceItemId,
          item_name: addon.name,
          affiliation: hotel.name,
          index: index,
          item_brand: hotel.master_item?.name,
          item_category: 'meeting rooms addons',
          item_list_id: 'addons_list',
          item_list_name: 'Addons List',
          item_variant: '',
          attendees_bucket: attendees_bucket,
          quantity: 1,
        }
      }),
    })
  }
}

/**
 * onClick on trash icon
 * @param eventId(string) - card event id
 * @param addonId(string) - row addonId
 * delete selected addon from cardAddons
 */
const deleteAddon = (eventId: string, addonId: string) => {
  if (cardAddons.value && cardAddons.value[eventId] && cardAddons.value[eventId].length > 0) {
    const packagesStore = usePackagesStore()
    const hotel = window.db2BookingFunnel.getHotel()
    let currentAddon = cartSummary.value?.addons.find((item) => addonId === item.addOnID)
    if (currentAddon) {
      let attendees_bucket = ''
      if (packagesStore.attendees >= 0 && packagesStore.attendees <= 5) attendees_bucket = '0-5'
      if (packagesStore.attendees >= 6 && packagesStore.attendees <= 10) attendees_bucket = '6-10'
      if (packagesStore.attendees >= 11 && packagesStore.attendees <= 20) attendees_bucket = '11-20'
      if (packagesStore.attendees >= 21 && packagesStore.attendees <= 30) attendees_bucket = '21-30'
      if (packagesStore.attendees > 30) attendees_bucket = '+30'

      event('remove_from_cart', {
        currency: cartSummary.value?.currencyCode,
        items: {
          item_id: addonId,
          item_name: currentAddon.name,
          affiliation: hotel?.name,
          index: 0,
          item_brand: hotel?.master_item?.name,
          item_category: 'meeting rooms addons',
          item_list_id: 'addons_list',
          item_list_name: 'Addons List',
          item_variant: '',
          attendees_bucket: attendees_bucket,
          quantity: 1,
        },
      })
    }

    cardAddons.value[eventId].map((addon, index) => {
      if (String(addonId) === String(addon.addonId)) {
        cardAddons.value[eventId].splice(index, 1)
      }
    })
  }
  cartStore.setAddons(cardAddons.value)
}

/**
 * onClick on confirm selection button on modal
 * @param isInCart: {id: string, qty: number}[]
 * Update or add selected addons to the cart selection
 */
const addToCart = (isInCart: { id: string; qty: number; currencyCode: string }[]) => {
  // get selected addons from current event from packageEvents
  const eventData = packageEvents.value?.filter(
    (data: { id: string }) => data.id === currentEventID.value,
  )
  if (!eventData) {
    throw new Error('data issue')
  }
  if (!eventData[0].addOnItems) {
    return
  }
  // init cardAddons
  cardAddons.value[currentEventID.value] = []

  // get addons data based on addonId in eventData
  eventData[0].addOnItems.filter(
    (addon: { sourceItemId: string; name: string; unitPrice: number }) => {
      const addonId = addon.sourceItemId // addon id

      isInCart.map((inCart) => {
        if (inCart.id === addonId) {
          cardAddons.value[currentEventID.value].push({
            addonId: addon.sourceItemId,
            name: addon.name,
            price: addon.unitPrice,
            quantity: inCart.qty,
            currencyCode: inCart.currencyCode,
          })
        }
      })
    },
  )

  cartStore.setAddons(cardAddons.value)

  // close modal when confirm selection
  if (modals) {
    modals.isOpen = false
  }
}

// Use to determine if the dynamic local should be use or not
// Depending on selectivity of "function room setup"
const isConfigurableFunctionRoom = () => {
  // Function Room Setup should not be selectable
  return !(functionRoomSetupOptions && functionRoomSetupOptions.length <= 1)
}

// Use to determine if the dynamic local should be use or not
// Depending on selectivity of "addOns"
const isConfigurableAddOns = () => {
  // All the package should not have any selectable addOn
  return (
    packageEvents.value?.filter((packageAddon) => {
      // "HasAddOn" or not
      return (packageAddon.addOnItems?.filter((item) => item.unitPrice > 0).length ?? 0) > 0
      // || packageAddon.items.length !== 0 ??? --> Impact or not
    }).length !== 0
  )
}

onMounted(() => {
  if (!isConfigurableFunctionRoom() && !isConfigurableAddOns()) {
    isEventConfigurable.value = false
  } else {
    isEventConfigurable.value = true
  }
})

onUnmounted(() => {
  isEventConfigurable.value = true
})
</script>

<style>
@import '@/assets/styles/components/modals/_modal-addons.css';
@import '@/assets/styles/components/app-containers/_package-configuration.css';
</style>
